/**
 * 站別目錄（platform catalog）——**全站唯一一份「有哪些站」的清單**。
 *
 * 為什麼要這一支：原本 `types.ts` 把 `Service` 寫成 `'ek21' | 'ut'`，`SERVICES`
 * 又是常數陣列，於是接了第三個站（wootalk）之後，畫面上選不到、後端回來的
 * session 也被悄悄改寫成 `ek21`（`ut` 以外一律 fallback）。新增一個站要動前端
 * 型別、常數、下拉、徽章……六、七處，難怪没人加。
 *
 * 現在：
 *   1. **新增一個站只改這個檔案的 `PLATFORM_CATALOG`**（一行）。
 *   2. 後端若有 `GET /api/platforms`（回 `{platforms:[{id,label,enabled,hint?}]}`），
 *      `applyRemoteCatalog()` 會把結果併進來——後端加站，前端不用重發版。
 *   3. **未知的站不會消失**：`serviceLabel()` 對沒登記的 id 直接回 id 本身，
 *      下拉也看得到，標「未啟用」而不是被吞掉。
 */
import { useSyncExternalStore } from 'react';
/**
 * 預設目錄。**新增站就加這裡一行**；`enabled:false` 的站會以「未啟用」出現在下拉，
 * 不會讓人以為站不見了。
 */
export const PLATFORM_CATALOG = [
    { id: 'ek21', label: 'ek21', enabled: true, hint: '一次 ≤3 顆 · 兩批間隔 20 秒' },
    { id: 'ut', label: 'UT', enabled: true, hint: '一次 ≤3 顆 · 兩批間隔 20 秒 · 進房後用 UT 暱稱' },
    { id: 'wootalk', label: 'WooTalk', enabled: false, hint: '本機 runner 配對，沒有房號' },
    { id: 'sg', label: 'SG', enabled: false, hint: '走 SG 平台金鑰（`/api/sg/*`），不在帳號池' },
];
let catalog = [...PLATFORM_CATALOG];
const listeners = new Set();
function emit() {
    for (const listener of listeners)
        listener();
}
/** 目前有效的目錄（含未啟用的站）。 */
export function listPlatforms() {
    return catalog;
}
/** 只用於測試與 `applyRemoteCatalog()`；外部請走 `setPlatformCatalog()`。 */
export function setPlatformCatalog(next) {
    catalog = next.map((item) => ({ ...item }));
    emit();
}
/**
 * 把後端回的目錄併進來。規則：
 * - 後端**有回**的站以它為準（label／enabled／hint）。
 * - 後端**沒提到**、但本地目錄有的站保留（避免後端舊版把站吃掉）。
 * - 後端多出來的站直接加入（這就是「新增站不用改前端」）。
 */
export function mergeServiceMeta(remote) {
    const byId = new Map();
    for (const item of catalog)
        byId.set(item.id, { ...item });
    for (const raw of remote) {
        const id = String(raw.id ?? raw.service ?? '').trim();
        if (id === '')
            continue;
        const current = byId.get(id);
        byId.set(id, {
            id,
            label: typeof raw.label === 'string' && raw.label !== '' ? raw.label : (current?.label ?? defaultLabel(id)),
            enabled: raw.enabled === undefined ? (current?.enabled ?? true) : raw.enabled !== false,
            ...(typeof raw.hint === 'string' ? { hint: raw.hint } : current?.hint !== undefined ? { hint: current.hint } : {}),
        });
    }
    return [...byId.values()];
}
/** 後端回了目錄就套用；回空陣列＝沒有這個端點，維持本地目錄。 */
export function applyRemoteCatalog(remote) {
    if (!Array.isArray(remote) || remote.length === 0)
        return;
    setPlatformCatalog(mergeServiceMeta(remote));
}
/** 沒登記過的站：直接用 id 當名稱（`wootalk` → `wootalk`），但已知大寫慣例照舊。 */
function defaultLabel(id) {
    return id.toUpperCase() === 'UT' ? 'UT' : id;
}
/** 站的顯示名稱；未知的站回 id 本身，**永不回 undefined**。 */
export function serviceLabel(id) {
    if (!serviceId(id))
        return '';
    return PLATFORM_CATALOG_LABELS().get(serviceId(id)) ?? serviceId(id);
}
function PLATFORM_CATALOG_LABELS() {
    const map = new Map();
    for (const item of catalog)
        map.set(item.id, item.label);
    return map;
}
/** 收斂成乾淨字串（空白／null 都當沒有）。 */
export function serviceId(raw) {
    return typeof raw === 'string' ? raw.trim() : '';
}
/**
 * 後端回來的 service 值 → 前端字串。**不再把 `ut` 以外的站改寫成 `ek21`**：
 * 認不得就原樣保留（例如 `wootalk`），壞掉的是顯示名稱，不是資料。
 */
export function normalizeService(raw, fallback = 'ek21') {
    const id = serviceId(raw);
    return id === '' ? fallback : id;
}
export function isServiceEnabled(id) {
    return catalog.find((item) => item.id === id)?.enabled === true;
}
export function platformHint(id) {
    return catalog.find((item) => item.id === id)?.hint;
}
/** 可操作的站（註冊／開通／建 session 只能挑這些）。 */
export function enabledPlatforms() {
    return catalog.filter((item) => item.enabled);
}
export const getPoolFilters = poolFilters;
export const serviceHint = platformHint;
export function enabledServices() {
    return enabledPlatforms().map((item) => item.id);
}
/** 帳號池篩選：`all` ＋ 可操作的站（順序照目錄）。 */
export function poolFilters() {
    return ['all', ...enabledServices()];
}
/** 目錄有變（後端回新站）時重畫。 */
export function subscribePlatforms(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
/** 元件用：跟著目錄更新。 */
export function usePlatforms() {
    return useSyncExternalStore(subscribePlatforms, listPlatforms, listPlatforms);
}
//# sourceMappingURL=platforms.js.map